import type {ContentRelationshipField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicRichText} from '@prismicio/react'
import Image from 'next/image'
import type {AuthorDocumentData} from 'prismicio-types'
import type {FC} from 'react'
import {Icon} from 'src/common/Icon'

export interface AuthorProfileProps {
  author: ContentRelationshipField<'author'>
  authorTitle: string
}

/**
 * Author profile section with photo, name, and summary
 * Matches BlogPost AuthorBio structure and styling
 */
export const AuthorProfile: FC<AuthorProfileProps> = ({author, authorTitle}) => {
  if (!isFilled.contentRelationship(author) || !author.data) {
    return null
  }

  const authorData = author.data as AuthorDocumentData

  return (
    <div className="flex flex-col gap-6 rounded-lg border border-moss-grey-150 bg-moss-grey-100 p-6">
      <div className="flex gap-3">
        <Image
          src={authorData.photo.url || ''}
          className="size-10 shrink-0 rounded-full object-cover"
          alt={authorData.photo.alt || 'Author image'}
          width={40}
          height={40}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div className="flex items-start justify-between">
            <div className="flex flex-col">
              <p className="text-15 font-normal leading-[18px] text-moss-grey-400">{authorTitle}</p>
              <p className="text-15 font-medium leading-[18px] text-moss-black">{authorData.name}</p>
            </div>
            {isFilled.link(authorData.linkedin_link) && (
              <a
                href={authorData.linkedin_link.url || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="group shrink-0"
              >
                <Icon name="linkedin-logo" className="size-6 text-grey-300 group-hover:brightness-[1.15]" />
              </a>
            )}
          </div>
          {isFilled.richText(authorData.summary) && (
            <PrismicRichText
              field={authorData.summary}
              components={{
                paragraph: ({children}) => (
                  <p className="text-15 font-normal leading-[20px] text-moss-grey-400">{children}</p>
                ),
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}
